import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import type { StudioStatus, StudiosResponseDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createAdminStudio, fetchAdminStudios, patchAdminStudio } from "../../shared/api/adminDirectory";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/lessons", label: "レッスン一覧" }
];

type StudioFormState = {
  displayName: string;
  shortName: string;
  address: string;
  mapUrl: string;
  status: StudioStatus;
  sortOrder: string;
};

type StudioEditState = StudioFormState & {
  studioId: string;
};

function createInitialStudioFormState(): StudioFormState {
  return {
    displayName: "",
    shortName: "",
    address: "",
    mapUrl: "",
    status: "active",
    sortOrder: "10"
  };
}

function toEditStateMap(studios: StudiosResponseDto | null): Record<string, StudioEditState> {
  if (!studios) {
    return {};
  }

  return Object.fromEntries(
    studios.items.map((studio) => [
      studio.studioId,
      {
        studioId: studio.studioId,
        displayName: studio.displayName,
        shortName: studio.shortName ?? "",
        address: studio.address ?? "",
        mapUrl: studio.mapUrl ?? "",
        status: studio.status,
        sortOrder: String(studio.sortOrder)
      }
    ])
  );
}

export function AdminRecurringSlotsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [studios, setStudios] = useState<StudiosResponseDto | null>(null);
  const [studioEdits, setStudioEdits] = useState<Record<string, StudioEditState>>({});
  const [createStudioForm, setCreateStudioForm] = useState<StudioFormState>(createInitialStudioFormState());
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const nextStudios = await fetchAdminStudios(tenantSlug);
      setStudios(nextStudios);
      setStudioEdits(toEditStateMap(nextStudios));
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.studio}の取得に失敗しました`));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshPage();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  async function handleCreateStudio(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create-studio");
    setLoadError(null);

    try {
      await createAdminStudio(tenantSlug, {
        displayName: createStudioForm.displayName.trim(),
        shortName: createStudioForm.shortName.trim() || null,
        address: createStudioForm.address.trim() || null,
        mapUrl: createStudioForm.mapUrl.trim() || null,
        status: createStudioForm.status,
        sortOrder: Number.parseInt(createStudioForm.sortOrder, 10) || 0
      });
      setCreateStudioForm(createInitialStudioFormState());
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.studio}の作成に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleUpdateStudio(studioId: string) {
    const editState = studioEdits[studioId];

    if (!editState) {
      return;
    }

    setBusyKey(`update-studio:${studioId}`);
    setLoadError(null);

    try {
      await patchAdminStudio(tenantSlug, studioId, {
        displayName: editState.displayName.trim(),
        shortName: editState.shortName.trim() || null,
        address: editState.address.trim() || null,
        mapUrl: editState.mapUrl.trim() || null,
        status: editState.status,
        sortOrder: Number.parseInt(editState.sortOrder, 10) || 0
      });
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.studio}の更新に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={tenantLabels.studio}
      description={`${tenantLabels.studio}の作成・一覧・更新を行います。`}
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>スタジオを読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid #d7e5ff",
                  background: "#f7fbff",
                  display: "grid",
                  gap: 8
                }}
              >
                <strong>運用の流れ</strong>
                <div style={{ fontSize: 14 }}>
                  1. スタジオを作成 2. レッスンを作成 3. 定期枠はレッスン詳細から管理 4. スケジュール月で生成・公開を行います
                </div>
                <div style={{ fontSize: 14 }}>
                  <Link to={`/t/${tenantSlug}/teacher/admin/lessons`}>レッスン一覧へ進む</Link>
                </div>
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                定期枠はレッスン詳細から管理できます。このページではスタジオのみを扱います。
              </p>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>スタジオ作成</h2>
              <form
                onSubmit={(event) => void handleCreateStudio(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>スタジオ名</span>
                  <input
                    value={createStudioForm.displayName}
                    onChange={(event) =>
                      setCreateStudioForm((current) => ({ ...current, displayName: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>略称</span>
                  <input
                    value={createStudioForm.shortName}
                    onChange={(event) =>
                      setCreateStudioForm((current) => ({ ...current, shortName: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>住所</span>
                  <input
                    value={createStudioForm.address}
                    onChange={(event) =>
                      setCreateStudioForm((current) => ({ ...current, address: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>地図URL</span>
                  <input
                    value={createStudioForm.mapUrl}
                    onChange={(event) =>
                      setCreateStudioForm((current) => ({ ...current, mapUrl: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>ステータス</span>
                  <select
                    value={createStudioForm.status}
                    onChange={(event) =>
                      setCreateStudioForm((current) => ({
                        ...current,
                        status: event.target.value === "inactive" ? "inactive" : "active"
                      }))
                    }
                  >
                    <option value="active">{describeStatus("active")}</option>
                    <option value="inactive">{describeStatus("inactive")}</option>
                  </select>
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>表示順</span>
                  <input
                    value={createStudioForm.sortOrder}
                    onChange={(event) =>
                      setCreateStudioForm((current) => ({ ...current, sortOrder: event.target.value }))
                    }
                  />
                </label>
                <button type="submit" disabled={busyKey === "create-studio"}>
                  {busyKey === "create-studio" ? "作成中..." : "スタジオを作成"}
                </button>
              </form>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>スタジオ一覧</h2>
              {studios?.items.length === 0 ? <p style={{ margin: 0 }}>スタジオはありません。</p> : null}
              {studios?.items.map((studio) => {
                const editState = studioEdits[studio.studioId];

                if (!editState) {
                  return null;
                }

                return (
                  <article
                    key={studio.studioId}
                    style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 10 }}
                  >
                    <div style={{ display: "grid", gap: 4 }}>
                      <strong>{studio.displayName}</strong>
                      <span style={{ fontSize: 12, opacity: 0.8 }}>{studio.studioId}</span>
                    </div>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>スタジオ名</span>
                      <input
                        value={editState.displayName}
                        onChange={(event) =>
                          setStudioEdits((current) => ({
                            ...current,
                            [studio.studioId]: { ...editState, displayName: event.target.value }
                          }))
                        }
                      />
                    </label>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>略称</span>
                      <input
                        value={editState.shortName}
                        onChange={(event) =>
                          setStudioEdits((current) => ({
                            ...current,
                            [studio.studioId]: { ...editState, shortName: event.target.value }
                          }))
                        }
                      />
                    </label>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>住所</span>
                      <input
                        value={editState.address}
                        onChange={(event) =>
                          setStudioEdits((current) => ({
                            ...current,
                            [studio.studioId]: { ...editState, address: event.target.value }
                          }))
                        }
                      />
                    </label>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>地図URL</span>
                      <input
                        value={editState.mapUrl}
                        onChange={(event) =>
                          setStudioEdits((current) => ({
                            ...current,
                            [studio.studioId]: { ...editState, mapUrl: event.target.value }
                          }))
                        }
                      />
                    </label>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>ステータス</span>
                      <select
                        value={editState.status}
                        onChange={(event) =>
                          setStudioEdits((current) => ({
                            ...current,
                            [studio.studioId]: {
                              ...editState,
                              status: event.target.value === "inactive" ? "inactive" : "active"
                            }
                          }))
                        }
                      >
                        <option value="active">{describeStatus("active")}</option>
                        <option value="inactive">{describeStatus("inactive")}</option>
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 4 }}>
                      <span>表示順</span>
                      <input
                        value={editState.sortOrder}
                        onChange={(event) =>
                          setStudioEdits((current) => ({
                            ...current,
                            [studio.studioId]: { ...editState, sortOrder: event.target.value }
                          }))
                        }
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => void handleUpdateStudio(studio.studioId)}
                      disabled={busyKey === `update-studio:${studio.studioId}`}
                    >
                      {busyKey === `update-studio:${studio.studioId}` ? "更新中..." : "スタジオを更新"}
                    </button>
                  </article>
                );
              })}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
